import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  TrashIcon,
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  EyeSlashIcon,
  EyeIcon,
  Square2StackIcon,
  RectangleStackIcon,
  ArrowUpRightIcon,
  ArrowDownLeftIcon,
  ArrowLeftCircleIcon,
  ArrowRightCircleIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/24/outline';
import { OBJECT_TYPES, CORRIDOR_WALL_THICKNESS_M, GRID_CELL_SIZE_M, MODES, MAX_FRAMELESS_WIDTH_M } from '../configuratorConstants';
import { useTranslation } from 'react-i18next';
import { distance } from '../configuratorUtils';
import elementService from '../../api/elementService';

const ActionButton = ({ onClick, title, children, className = '', disabled = false }) => (
  <button
    onClick={onClick}
    title={title}
    disabled={disabled}
    className={`w-full flex items-center justify-center p-2.5 text-sm font-medium rounded-lg shadow-sm transition-all duration-150 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-blue ${disabled
      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
      : 'text-gray-700 bg-white hover:bg-gray-100'
      } ${className}`}
  >
    {children}
  </button>
);

const Section = ({ title, children }) => (
  <div className="mt-4 pt-4 border-t border-gray-200">
    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{title}</h3>
    <div className="space-y-2">
      {children}
    </div>
  </div>
);

const PropertiesPanel = ({ primarySelectedObject, dispatch, jointObject, state, onStartModuleMove }) => {
  const { t } = useTranslation();
  const [dbElements, setDbElements] = useState([]);
  const [elementsLoaded, setElementsLoaded] = useState(false);

  // Загружаем элементы из БД один раз
  useEffect(() => {
    const loadElements = async () => {
      if (elementsLoaded) return;
      try {
        const response = await elementService.getElementsForConfigurator();
        const elements = response.data || [];
        setDbElements(elements);
        setElementsLoaded(true);
        if (process.env.NODE_ENV !== "production") {
          console.log('📦 [PropertiesPanel] Элементы загружены из БД:', {
            count: elements.length,
            elements: elements.map(el => ({ code: el.elementCode, name: el.elementName, isDoor: el.isDoor }))
          });
        }
      } catch (error) {
        console.error('Ошибка при загрузке элементов:', error);
        setElementsLoaded(true); // Помечаем как загруженные, чтобы не повторять запросы
      }
    };
    loadElements();
  }, [elementsLoaded]);

  if (!primarySelectedObject) {
    return null;
  }

  const {
    type, label, id, parentJointId, thickness,
    parentModuleId, cellX, cellY, orientation,
    parentModuleCellsWide, parentModuleCellsLong,
    isExternal, parentPartitionId, parentWallId,
    parentWallSegment,
  } = primarySelectedObject;

  const isModule = type === OBJECT_TYPES.MODULE;
  const isWallSegment = type === OBJECT_TYPES.WALL_SEGMENT;
  const isJointWallSegment = type === OBJECT_TYPES.JOINT_WALL_SEGMENT;
  const isDockingJoint = type === OBJECT_TYPES.DOCKING_JOINT;
  const isElementOnJoint = !!parentJointId;
  const isElementOnPartition = !!parentPartitionId;
  const isElementOnModuleWallSegment = !!(parentModuleId && parentWallSegment);

  // Проверяем, является ли элемент дверью
  // Сначала проверяем стандартный тип двери
  let isDoor = type === OBJECT_TYPES.DOOR;

  // Если это не стандартная дверь, проверяем элементы из БД
  if (!isDoor && type) {
    // Проверяем, является ли type элементом из БД (не стандартным OBJECT_TYPES)
    const isStandardType = Object.values(OBJECT_TYPES).includes(type);
    if (!isStandardType) {
      // Ищем элемент в БД по elementCode (который хранится в type)
      const dbElement = dbElements.find(el => el.elementCode === type);
      if (dbElement) {
        if (process.env.NODE_ENV !== "production") {
          console.log('🚪 [PropertiesPanel] Проверка элемента из БД:', {
            elementCode: type,
            isDoor: dbElement.isDoor,
            elementName: dbElement.elementName
          });
        }
        if (dbElement.isDoor) {
          isDoor = true;
        }
      } else if (process.env.NODE_ENV !== "production") {
        console.log('⚠️ [PropertiesPanel] Элемент не найден в БД:', {
          elementCode: type,
          dbElementsCount: dbElements.length,
          loadedElements: dbElements.map(el => el.elementCode)
        });
      }
    }
  }

  if (process.env.NODE_ENV !== "production" && isDoor) {
    console.log('✅ [PropertiesPanel] Элемент определен как дверь:', {
      type,
      isDoor,
      isStandardDoor: type === OBJECT_TYPES.DOOR
    });
  }

  let isInternalNonDockedWall = false;
  if (isWallSegment && parentModuleId && typeof cellX === 'number' && typeof cellY === 'number' && orientation && typeof parentModuleCellsWide === 'number' && typeof parentModuleCellsLong === 'number') {
    const isPerimeter = (orientation === 'h' && (cellY === 0 || cellY === parentModuleCellsLong)) ||
      (orientation === 'v' && (cellX === 0 || cellX === parentModuleCellsWide));

    if (!isPerimeter && !primarySelectedObject.isDocked) {
      isInternalNonDockedWall = true;
    }
  }


  const panelVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { type: "spring", stiffness: 300, damping: 30 } },
    exit: { opacity: 0, x: 50, transition: { duration: 0.15 } },
  };

  // Проверяет, является ли безкаркасная стена частью завершенной комнаты
  const isWallPartOfClosedRoom = (wallId) => {
    if (!state?.projectObjects || !state?.currentFloorId) return false;

    const floorObjects = state.projectObjects[state.currentFloorId] || [];
    const allWalls = floorObjects.filter(obj =>
      obj.type === OBJECT_TYPES.FRAMELESS_WALL && obj.startPoint && obj.endPoint
    );

    if (allWalls.length < 3) return false; // Минимум 3 стены для треугольника

    const epsilon = 0.01;

    // Находим удаляемую стену
    const targetWall = allWalls.find(w => w.id === wallId);
    if (!targetWall) return false;

    // Функция для получения следующей точки стены
    const getNextPoint = (wall, point) => {
      const distToStart = distance(point, wall.startPoint);
      const distToEnd = distance(point, wall.endPoint);
      if (distToStart < epsilon) {
        return wall.endPoint;
      } else if (distToEnd < epsilon) {
        return wall.startPoint;
      }
      return null;
    };

    // Пробуем найти замкнутый цикл, содержащий эту стену
    const startWall = targetWall;
    const visited = new Set();
    let foundClosedLoop = false;

    // Пробуем оба направления для начальной стены
    for (const startPoint of [startWall.startPoint, startWall.endPoint]) {
      const currentPoint = getNextPoint(startWall, startPoint);
      if (!currentPoint) continue;

      const path = [startWall];
      visited.clear();
      visited.add(startWall.id);

      const dfs = (currentWall, currentEnd) => {
        // Проверяем, можем ли замкнуть цикл
        if (path.length >= 4) {
          const distToStart = distance(currentEnd, startPoint);
          if (distToStart < epsilon) {
            // Нашли замкнутый цикл, содержащий удаляемую стену
            foundClosedLoop = true;
            return true;
          }
        }

        if (path.length >= 10) return false; // Ограничение глубины

        // Ищем следующую стену, которая соединяется с текущей точкой
        for (const nextWall of allWalls) {
          if (visited.has(nextWall.id)) continue;

          const nextPoint = getNextPoint(nextWall, currentEnd);
          if (nextPoint) {
            visited.add(nextWall.id);
            path.push(nextWall);
            if (dfs(nextWall, nextPoint)) {
              return true;
            }
            path.pop();
            visited.delete(nextWall.id);
          }
        }

        return false;
      };

      if (dfs(startWall, currentPoint)) {
        break;
      }
    }

    return foundClosedLoop;
  };

  const isDeletable = () => {
    if (type === OBJECT_TYPES.DOCKING_JOINT) {
      return false;
    }
    if (type === OBJECT_TYPES.WALL_SEGMENT && isExternal) {
      return false;
    }
    if (state?.activeMode === MODES.FRAMELESS) {
      if (
        type === OBJECT_TYPES.FRAMELESS_WALL ||
        type === OBJECT_TYPES.FRAMELESS_PARTITION
      ) {
        // Проверяем, является ли стена частью завершенной комнаты
        // Если да - нельзя удалять, если нет - можно
        return !isWallPartOfClosedRoom(id);
      }
      if (type === 'frameless_wall_segment') {
        // Для сегментов проверяем родительскую стену
        const parentWallId = primarySelectedObject.parentWallId;
        if (parentWallId) {
          return !isWallPartOfClosedRoom(parentWallId);
        }
        return true;
      }
    }
    return true;
  };

  const handleDeleteJointWallSegment = () => {
    if (isJointWallSegment) {
      const { jointId, segmentIndex } = primarySelectedObject;
      const joint = primarySelectedObject.joint;
      if (joint) {
        const numSlots = Math.max(1, Math.round(joint.width / GRID_CELL_SIZE_M));
        const positionOnSegment = (segmentIndex + 0.5) / numSlots;
        dispatch({
          type: "TOGGLE_WALL_SEGMENT_IN_JOINT",
          payload: {
            jointId,
            positionOnSegment,
          },
        });
      }
    }
  };

  const renderModuleControls = () => {
    // Проверяем, является ли выбранный модуль первым размещенным модулем
    // и единственным модулем на этаже
    const currentFloorObjects = state?.projectObjects?.[state.currentFloorId] || [];
    const modulesOnFloor = currentFloorObjects.filter(
      (obj) => obj.type === OBJECT_TYPES.MODULE,
    );
    const firstModule = modulesOnFloor.length > 0 ? modulesOnFloor[0] : null;
    const isFirstModule = firstModule && firstModule.id === id;
    const isOnlyModule = modulesOnFloor.length === 1;
    const canRotate = isFirstModule && isOnlyModule;

    // Определяем текущую ориентацию модуля и текст кнопки
    let buttonText;
    let buttonTitle;

    if (canRotate) {
      // Определяем ориентацию по размерам модуля, а не по rotation
      const cellsWide = primarySelectedObject.cellsWide || 2;
      const cellsLong = primarySelectedObject.cellsLong || 5;
      const isHorizontal = cellsWide > cellsLong;

      buttonText = isHorizontal
        ? t('propertiesPanel.makeVertical')
        : t('propertiesPanel.makeHorizontal');
      buttonTitle = isHorizontal
        ? t('propertiesPanel.makeVerticalTitle')
        : t('propertiesPanel.makeHorizontalTitle');
    } else {
      buttonText = t('propertiesPanel.rotateOnlyFirstModule');
      buttonTitle = t('propertiesPanel.rotateOnlyFirstModule');
    }

    return (
      <Section title={t('propertiesPanel.moduleActions')}>
        <ActionButton
          onClick={() => dispatch({ type: 'ROTATE_SELECTED_MODULE' })}
          title={
            canRotate
              ? buttonTitle
              : isFirstModule
                ? t('propertiesPanel.cannotRotateFirstWithOthers')
                : buttonTitle
          }
          disabled={!canRotate}
        >
          <ArrowPathIcon className="w-5 h-5 mr-2" />
          <span>{buttonText}</span>
        </ActionButton>
        {onStartModuleMove && (
          <ActionButton
            onClick={() => {
              const module = primarySelectedObject;
              if (module && module.type === OBJECT_TYPES.MODULE) {
                onStartModuleMove(module);
              }
            }}
            title={t('propertiesPanel.moveModuleTitle', { defaultValue: 'Перемістити модуль до іншого місця' })}
          >
            <ArrowsUpDownIcon className="w-5 h-5 mr-2" />
            <span>{t('propertiesPanel.moveModule', { defaultValue: 'Перемістити' })}</span>
          </ActionButton>
        )}
      </Section>
    );
  };

  const renderDockingControls = () => {
    if (!isDockingJoint) return null;

    const { displayMode = 'double', passageSegments = [] } = primarySelectedObject;
    const setMode = (mode) => {
      const payload = { jointId: id, mode };
      dispatch({ type: 'SET_DOCKING_JOINT_DISPLAY_MODE', payload });
    };

    const togglePassageOnSegment = () => {
      // Toggle прохода на кликнутом сегменте
      const segmentIndex = state.clickedJointSegmentIndex;
      if (segmentIndex !== null && segmentIndex !== undefined) {
        dispatch({
          type: 'TOGGLE_JOINT_PASSAGE_SEGMENT',
          payload: { jointId: id, segmentIndex }
        });
      }
    };

    return (
      <Section title={t('propertiesPanel.jointManagement')}>
        {displayMode === 'double' && (
          <>
            <ActionButton onClick={() => setMode('single')} title={t('propertiesPanel.mergeToSingleWallTitle')}>
              <RectangleStackIcon className="w-5 h-5 mr-2" />
              <span>{t('propertiesPanel.mergeToSingleWall')}</span>
            </ActionButton>
            <ActionButton onClick={() => setMode('passage')} title={t('propertiesPanel.createPassageTitle')}>
              <EyeSlashIcon className="w-5 h-5 mr-2 text-orange-600" />
              <span>{t('propertiesPanel.createPassage')}</span>
            </ActionButton>
            <ActionButton onClick={togglePassageOnSegment} title={t('propertiesPanel.createSinglePassageTitle')}>
              <RectangleStackIcon className="w-5 h-5 mr-2 text-blue-600" />
              <span>{t('propertiesPanel.createSinglePassage')}</span>
            </ActionButton>
          </>
        )}
        {displayMode === 'single' && (
          <>
            <ActionButton onClick={() => setMode('double')} title={t('propertiesPanel.splitToDoubleWallTitle')}>
              <Square2StackIcon className="w-5 h-5 mr-2" />
              <span>{t('propertiesPanel.splitToDoubleWall')}</span>
            </ActionButton>
            <ActionButton onClick={() => setMode('passage')} title={t('propertiesPanel.createPassageTitle')}>
              <EyeSlashIcon className="w-5 h-5 mr-2 text-orange-600" />
              <span>{t('propertiesPanel.createPassage')}</span>
            </ActionButton>
            <ActionButton onClick={togglePassageOnSegment} title={t('propertiesPanel.createSinglePassageTitle')}>
              <RectangleStackIcon className="w-5 h-5 mr-2 text-blue-600" />
              <span>{t('propertiesPanel.createSinglePassage')}</span>
            </ActionButton>
          </>
        )}
        {displayMode === 'passage' && (
          <>
            <ActionButton onClick={() => setMode('single')} title={t('propertiesPanel.restoreSingleWallTitle')}>
              <EyeIcon className="w-5 h-5 mr-2 text-green-600" />
              <span>{t('propertiesPanel.restoreSingleWall')}</span>
            </ActionButton>
            <ActionButton onClick={() => setMode('double')} title={t('propertiesPanel.restoreDoubleWallTitle')}>
              <EyeIcon className="w-5 h-5 mr-2 text-green-600" />
              <span>{t('propertiesPanel.restoreDoubleWall')}</span>
            </ActionButton>
          </>
        )}
        {displayMode === 'custom' && (
          <>
            <ActionButton onClick={() => setMode('single')} title={t('propertiesPanel.mergeToSingleWallTitle')}>
              <RectangleStackIcon className="w-5 h-5 mr-2" />
              <span>{t('propertiesPanel.mergeToSingleWall')}</span>
            </ActionButton>
            <ActionButton onClick={() => setMode('double')} title={t('propertiesPanel.splitToDoubleWallTitle')}>
              <Square2StackIcon className="w-5 h-5 mr-2" />
              <span>{t('propertiesPanel.splitToDoubleWall')}</span>
            </ActionButton>
            <ActionButton onClick={() => setMode('passage')} title={t('propertiesPanel.createPassageTitle')}>
              <EyeSlashIcon className="w-5 h-5 mr-2 text-orange-600" />
              <span>{t('propertiesPanel.createPassage')}</span>
            </ActionButton>
          </>
        )}
      </Section>
    );
  };

  const renderElementOnJointControls = () => {
    if (!isElementOnJoint || !jointObject || jointObject.displayMode !== 'double' || type === OBJECT_TYPES.DOOR) {
      return null;
    }

    return (
      <Section title={t('propertiesPanel.placementOnJoint')}>
        <ActionButton
          onClick={() => dispatch({ type: 'TOGGLE_ELEMENT_PLACEMENT_SIDE' })}
          title={t('propertiesPanel.moveToOtherSideTitle')}
        >
          <ArrowsRightLeftIcon className="w-5 h-5 mr-2" />
          <span>{t('propertiesPanel.moveToOtherSide')}</span>
        </ActionButton>
      </Section>
    );
  };

  const renderElementOnPartitionControls = () => {
    if (!isElementOnPartition || type === OBJECT_TYPES.DOOR) {
      return null;
    }

    // Показуємо кнопку тільки для внутрішніх стін (перегородок)
    let isInternalPartition = false;
    if (state?.projectObjects && state?.currentFloorId) {
      const floorObjects = state.projectObjects[state.currentFloorId] || [];
      if (parentPartitionId) {
        const parentPartition = floorObjects.find(
          obj => obj.id === parentPartitionId && obj.type === OBJECT_TYPES.FRAMELESS_PARTITION
        );
        if (parentPartition?.isInternal === true) isInternalPartition = true;
      }
      if (!isInternalPartition && parentWallId) {
        const parentWall = floorObjects.find(
          obj => obj.id === parentWallId && obj.type === OBJECT_TYPES.FRAMELESS_PARTITION
        );
        if (parentWall?.isInternal === true) isInternalPartition = true;
      }
    }
    if (!isInternalPartition) return null;

    return (
      <Section title={t('propertiesPanel.placementOnPartition')}>
        <ActionButton
          onClick={() => dispatch({ type: 'TOGGLE_ELEMENT_PLACEMENT_SIDE' })}
          title={t('propertiesPanel.moveToOtherSideTitle')}
        >
          <ArrowsRightLeftIcon className="w-5 h-5 mr-2" />
          <span>{t('propertiesPanel.moveToOtherSide')}</span>
        </ActionButton>
      </Section>
    );
  };

  const renderElementOnModuleWallSegmentControls = () => {
    if (!isElementOnModuleWallSegment || type === OBJECT_TYPES.DOOR) {
      return null;
    }

    // Кнопка тільки для внутрішніх стінок модуля (між двома кімнатами), не для зовнішніх
    if (parentWallSegment?.isExternal) return null;

    return (
      <Section title={t('propertiesPanel.placementOnJoint')}>
        <ActionButton
          onClick={() => dispatch({ type: 'TOGGLE_ELEMENT_PLACEMENT_SIDE' })}
          title={t('propertiesPanel.moveToOtherSideTitle')}
        >
          <ArrowsRightLeftIcon className="w-5 h-5 mr-2" />
          <span>{t('propertiesPanel.moveToOtherSide')}</span>
        </ActionButton>
      </Section>
    );
  };

  const renderDoorControls = () => {
    if (!isDoor) return null;

    const { openingDirection = 'outward', hingeSide = 'left' } = primarySelectedObject;

    const setProperty = (property, value) => {
      if (process.env.NODE_ENV !== "production") {
        console.log('🚪 [PropertiesPanel] Изменение свойства двери:', {
          elementId: id,
          property,
          value,
          currentOpeningDirection: primarySelectedObject.openingDirection,
          currentHingeSide: primarySelectedObject.hingeSide,
          elementType: type
        });
      }

      dispatch({
        type: 'SET_ELEMENT_PROPERTY',
        payload: { elementId: id, property, value }
      });
    };

    return (
      <Section title={t('propertiesPanel.doorSettings.title')}>
        <div className="grid grid-cols-2 gap-2">
          <ActionButton
            onClick={() => setProperty('openingDirection', 'outward')}
            title={t('propertiesPanel.doorSettings.openOutward')}
            className={openingDirection === 'outward' ? '!bg-primary-blue !text-white' : ''}
          >
            <ArrowUpRightIcon className="w-5 h-5 mr-2" />
            <span>{t('propertiesPanel.doorSettings.outward')}</span>
          </ActionButton>
          <ActionButton
            onClick={() => setProperty('openingDirection', 'inward')}
            title={t('propertiesPanel.doorSettings.openInward')}
            className={openingDirection === 'inward' ? '!bg-primary-blue !text-white' : ''}
          >
            <ArrowDownLeftIcon className="w-5 h-5 mr-2" />
            <span>{t('propertiesPanel.doorSettings.inward')}</span>
          </ActionButton>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <ActionButton
            onClick={() => setProperty('hingeSide', 'left')}
            title={t('propertiesPanel.doorSettings.hingeLeft')}
            className={hingeSide === 'left' ? '!bg-primary-blue !text-white' : ''}
          >
            <ArrowLeftCircleIcon className="w-5 h-5 mr-2" />
            <span>{t('propertiesPanel.doorSettings.left')}</span>
          </ActionButton>
          <ActionButton
            onClick={() => setProperty('hingeSide', 'right')}
            title={t('propertiesPanel.doorSettings.hingeRight')}
            className={hingeSide === 'right' ? '!bg-primary-blue !text-white' : ''}
          >
            <ArrowRightCircleIcon className="w-5 h-5 mr-2" />
            <span>{t('propertiesPanel.doorSettings.right')}</span>
          </ActionButton>
        </div>
      </Section>
    );
  };

  const renderJointWallSegmentControls = () => {
    if (!isJointWallSegment) return null;

    return (
      <Section title={t('propertiesPanel.wallSegmentActions')}>
        <ActionButton
          onClick={handleDeleteJointWallSegment}
          title={t('propertiesPanel.deleteWallSegmentTitle')}
          className="!text-red-600 hover:!bg-red-50"
        >
          <TrashIcon className="w-5 h-5 mr-2" />
          <span>{t('propertiesPanel.deleteWallSegment')}</span>
        </ActionButton>
      </Section>
    );
  };

  const renderInfo = () => {
    // Специальная обработка для frameless_wall_segment
    if (type === 'frameless_wall_segment') {
      const parentWallId = primarySelectedObject.parentWallId;
      let wallLength = 0;

      if (parentWallId && state?.projectObjects?.[state.currentFloorId]) {
        const floorObjects = state.projectObjects[state.currentFloorId];
        const parentWall = floorObjects.find(obj => obj.id === parentWallId);

        if (parentWall && parentWall.startPoint && parentWall.endPoint) {
          wallLength = distance(parentWall.startPoint, parentWall.endPoint);
        }
      }

      return (
        <div>
          <h2 className="text-lg font-bold text-gray-800 break-words">
            {label || 'Безкаркасна стінка'}
          </h2>
          <p className="text-xs text-gray-400 mt-1 break-all">ID: {id}</p>
          <div className="mt-3 text-sm text-gray-700 p-3 bg-blue-50 rounded-md border border-blue-200">
            <p className="font-medium text-gray-800 mb-1">
              Максимальна технологічна ширина стінки: {MAX_FRAMELESS_WIDTH_M} м
            </p>
            {wallLength > 0 && (
              <p className="text-gray-600 mb-2">
                Довжина цієї стінки: {wallLength.toFixed(2)} м
              </p>
            )}
            <p className="text-gray-600">
              Поставте перегородку для збільшення площі будівлі
            </p>
          </div>
        </div>
      );
    }

    return (
      <div>
        <h2 className="text-lg font-bold text-gray-800 break-words">{label || (isDockingJoint ? t('propertiesPanel.jointLabel') : (isWallSegment ? t('propertiesPanel.wallSegmentLabel') : type.replace(/_/g, ' ')))}</h2>
        <p className="text-xs text-gray-400 mt-1 break-all">ID: {id}</p>
        <p className="text-sm text-primary-blue mt-2">
          {isWallSegment ?
            (primarySelectedObject.isDocked ? t('propertiesPanel.dockedWall') :
              (isInternalNonDockedWall ? `${t('propertiesPanel.internalWall')} (${thickness === CORRIDOR_WALL_THICKNESS_M ? t('propertiesPanel.corridorWall') : t('propertiesPanel.standardWall')})` :
                t('propertiesPanel.externalWall')))
            : ''}
        </p>
        {isElementOnJoint && (
          <div className="mt-2 text-sm p-2 bg-gray-100 rounded-md border border-gray-200">
            <p className="font-medium text-gray-600">{t('propertiesPanel.locatedOn')} <span className="font-normal text-primary-blue">{t('propertiesPanel.jointLabel')}</span></p>
            {jointObject && <p className="text-xs text-gray-500">{t('propertiesPanel.jointId')} {jointObject.id}</p>}
          </div>
        )}
      </div>
    );
  };

  return (
    <motion.div
      key={id}
      variants={panelVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      id="properties-panel"
      className="w-72 bg-card-bg-light border-l border-gray-300 p-4 overflow-y-auto flex-shrink-0"
    >
      {renderInfo()}
      {isModule && renderModuleControls()}
      {isDockingJoint && renderDockingControls()}
      {isElementOnJoint && renderElementOnJointControls()}
      {isElementOnPartition && renderElementOnPartitionControls()}
      {isElementOnModuleWallSegment && renderElementOnModuleWallSegmentControls()}
      {isDoor && renderDoorControls()}
      {isJointWallSegment && renderJointWallSegmentControls()}

      {(() => {
        const isFramelessWall = type === OBJECT_TYPES.FRAMELESS_WALL;
        const isFramelessPartition = type === OBJECT_TYPES.FRAMELESS_PARTITION;
        const isFramelessWallSegment = type === 'frameless_wall_segment';
        const parentWallId = primarySelectedObject.parentWallId;

        // Проверяем, является ли перегородка внутренней стеной
        const isInternalWall = isFramelessPartition && primarySelectedObject.isInternal === true;

        // Определяем, принадлежит ли стена/сегмент закрытой комнате
        let isPartOfClosedRoom = false;
        let wallIdToCheck = null;

        if (isFramelessWall) {
          wallIdToCheck = id;
          isPartOfClosedRoom = isWallPartOfClosedRoom(id);
        } else if (isFramelessWallSegment && parentWallId) {
          wallIdToCheck = parentWallId;
          isPartOfClosedRoom = isWallPartOfClosedRoom(parentWallId);
        } else if (isFramelessPartition) {
          // Для перегородок проверяем, является ли она частью закрытой комнаты
          wallIdToCheck = id;
          isPartOfClosedRoom = isWallPartOfClosedRoom(id);
        }

        // Проверяем, является ли сегмент частью внутренней стены
        let isInternalWallSegment = false;
        if (isFramelessWallSegment && parentWallId && state?.projectObjects && state?.currentFloorId) {
          const floorObjects = state.projectObjects[state.currentFloorId] || [];
          const parentWall = floorObjects.find(
            obj => obj.id === parentWallId && obj.type === OBJECT_TYPES.FRAMELESS_PARTITION
          );
          if (parentWall && parentWall.isInternal === true) {
            isInternalWallSegment = true;
          }
        }

        // Если это внутренняя стена (перегородка) или сегмент внутренней стены - показываем только одну кнопку удаления
        if (isInternalWall || isInternalWallSegment) {
          return (
            <Section title={t('propertiesPanel.deletion')}>
              <ActionButton
                onClick={() => {
                  if (isInternalWallSegment && parentWallId) {
                    // Для сегмента внутренней стены удаляем весь объект (сэндвич)
                    // так как каждый сэндвич - это отдельный объект FRAMELESS_PARTITION
                    dispatch({ type: 'DELETE_WALL_SEGMENT', payload: { segmentId: id, wallId: parentWallId } });
                  } else {
                    // Удаляем всю внутреннюю стену
                    dispatch({ type: 'DELETE_SELECTED_OBJECT' });
                  }
                }}
                title={t('propertiesPanel.deleteTitle', { type: 'внутрішня стіна' })}
                className="!text-red-600 hover:!bg-red-50"
              >
                <TrashIcon className="w-5 h-5 mr-2" />
                <span>{t('propertiesPanel.delete')}</span>
              </ActionButton>
            </Section>
          );
        }

        // Если это сегмент и комната не закрыта - показываем две кнопки
        if (isFramelessWallSegment && !isPartOfClosedRoom && parentWallId) {
          return (
            <Section title={t('propertiesPanel.deletion')}>
              <ActionButton
                onClick={() => dispatch({ type: 'DELETE_WALL_SEGMENT', payload: { segmentId: id, wallId: parentWallId } })}
                title={t('propertiesPanel.deleteSegmentTitle', { defaultValue: 'Видалити тільки цей сендвіч' })}
                className="!text-red-600 hover:!bg-red-50"
              >
                <TrashIcon className="w-5 h-5 mr-2" />
                <span>{t('propertiesPanel.deleteSegment', { defaultValue: 'Видалити сендвіч' })}</span>
              </ActionButton>
              <ActionButton
                onClick={() => {
                  // Удаляем всю стену напрямую по её ID
                  dispatch({ type: 'DELETE_WALL_BY_ID', payload: { wallId: parentWallId } });
                }}
                title={t('propertiesPanel.deleteWallTitle', { defaultValue: 'Видалити всю стіну' })}
                className="!text-red-600 hover:!bg-red-50 mt-2"
              >
                <TrashIcon className="w-5 h-5 mr-2" />
                <span>{t('propertiesPanel.deleteWall', { defaultValue: 'Видалити всю стіну' })}</span>
              </ActionButton>
            </Section>
          );
        }

        // Если комната закрыта или это стена - показываем одну кнопку
        // Для замкнутой комнаты кнопка всегда активна (не проверяем isDeletable)
        return (
          <Section title={t('propertiesPanel.deletion')}>
            <ActionButton
              onClick={() => dispatch({ type: 'DELETE_SELECTED_OBJECT' })}
              title={
                isPartOfClosedRoom
                  ? t('propertiesPanel.deleteRoomTitle', { defaultValue: 'Видалити всю кімнату' })
                  : t('propertiesPanel.deleteTitle', { type: type.replace(/_/g, ' ') })
              }
              disabled={!isPartOfClosedRoom && !isDeletable()}
              className="!text-red-600 hover:!bg-red-50"
            >
              <TrashIcon className="w-5 h-5 mr-2" />
              <span>
                {isPartOfClosedRoom
                  ? t('propertiesPanel.deleteRoom', { defaultValue: 'Видалити кімнату' })
                  : t('propertiesPanel.delete')
                }
              </span>
            </ActionButton>
          </Section>
        );
      })()}

    </motion.div>
  );
};

export default PropertiesPanel;